import { useEffect, type FC, useState } from 'react';
import { Divider, Form, type FormInstance } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Text, Space } from '@g2u/ui-kit';
import { isEmpty, isEqual } from 'lodash';

import {
  HomeAddressFields,
  BillingAddressFields,
  PrimaryContactFields,
  SecondaryContactFields,
  AccountTypeFields,
  AccountNotesFields,
  CustomerExtraInfo,
} from 'components';
import { useText } from 'helpers/hooks/useText';
import { Toast, parseErrors, useCustomerProducts, useDiscountOptions } from 'helpers';
import { getSystemDiscounts, putCustomer } from 'queries';
import { AccountType, PrepaidProductStatus, type JsonDataItem, type WithOptionalDataTest } from 'types';
import styles from './confirm-customer-file.module.scss';

type Props = {
  hasScript?: boolean;
  hasDiscount?: boolean;
  form: FormInstance;
  customer: JsonDataItem | null;
  onUpdated: (data: JsonDataItem | null) => void;
  onLoading: (status: boolean) => void;
  isLocked?: boolean;
} & WithOptionalDataTest;

export const ConfirmCustomerFile: FC<Props> = (props) => {
  const { dataTestId, form, hasScript, customer, onUpdated, onLoading, hasDiscount, isLocked } = props;
  const [initialValues, setInitialValues] = useState<JsonDataItem>(form.getFieldsValue());
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const personalAccount = 'Personal';
  const [accountType, setAccountType] = useState<AccountType>(
    customer?.type === personalAccount ? AccountType.Personal : AccountType.Company,
  );
  const { getText } = useText();
  const { prepaidProducts } = useCustomerProducts(customer?.ID, PrepaidProductStatus.New);

  const { data: systemDiscounts } = useQuery(['service-discounts', customer?.ID], getSystemDiscounts, {
    enabled: hasDiscount && !!customer?.ID,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });
  const { getLabel } = useDiscountOptions(systemDiscounts);
  const customerDiscounts = systemDiscounts?.filter((discount: JsonDataItem) => discount.type === 'Customer');

  const { mutate: updateCustomer, isLoading: updatingCustomer } = useMutation(putCustomer, {
    onSuccess: (data) => {
      Toast.show({
        description: `New changes on customer file #${customer?.ID} have been saved.`,
        type: Toast.Type.Success,
      });
      setIsFormChanged(false);
      onUpdated(data);
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const handleUpdateCustomerFile = (formData: JsonDataItem) => {
    const payload = {
      id: customer?.ID,
      accountType: customer?.type === 'Company' ? AccountType.Company : AccountType.Personal,
      ...formData,
    };
    isFormChanged ? updateCustomer(payload) : onUpdated(customer);
  };

  useEffect(() => {
    onLoading(updatingCustomer);
  }, [updatingCustomer]);

  useEffect(() => {
    setInitialValues(form.getFieldsValue());
  }, []);

  return (
    <div className={styles.confirmCustomerFile} data-testid={dataTestId}>
      <Form
        form={form}
        name="confirm-form"
        layout="horizontal"
        className={styles.confirmCustomerFile__form}
        onFinish={handleUpdateCustomerFile}
        onValuesChange={(changedFields) => {
          setIsFormChanged(true);
          changedFields.accountType && setAccountType(changedFields.accountType);
          !isEmpty(initialValues) && !isEqual(form.getFieldsValue(), initialValues) && setIsFormChanged(true);
        }}
        onChange={() => {
          setIsFormChanged(true);
        }}
        onFinishFailed={(data) => {
          Toast.show({
            description: data.errorFields.flatMap((field) => field.errors).join(', '),
            type: Toast.Type.Error,
            duration: 0,
          });
        }}
        colon={false}
        preserve={false}
        disabled={isLocked}
      >
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          {hasScript && (
            <Text.$ className="script-box" mark>
              {getText('booking.location.visitLocation')}
            </Text.$>
          )}
          <AccountTypeFields inline customer={customer} />
        </Space.$>

        {hasDiscount && !!customerDiscounts?.length && (
          <CustomerExtraInfo
            type={CustomerExtraInfo.Type.Discount}
            items={customerDiscounts.map((discount: JsonDataItem) => ({
              label: `${getLabel(discount.discount_type, discount.amount)} ${discount.discount_desc}`,
            }))}
          />
        )}
        {prepaidProducts?.length > 0 && (
          <CustomerExtraInfo
            type={CustomerExtraInfo.Type.Prepaid}
            items={prepaidProducts.map((product: JsonDataItem) => ({
              label: product.product_name,
              tooltip: 'The customer has purchased Geeks2U services from Officeworks',
            }))}
          />
        )}
        {!hasDiscount && !prepaidProducts?.length && <Divider />}

        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          <HomeAddressFields inline form={form} customer={customer} />
        </Space.$>
        <Divider />

        <BillingAddressFields inline form={form} customer={customer} />
        <Divider />

        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          {hasScript && (
            <Text.$ className="script-box" mark>
              {getText('booking.start.updateContactDetails')}
            </Text.$>
          )}
          <PrimaryContactFields
            form={form}
            inline
            customer={customer}
            isBusinessAccount={accountType === AccountType.Company}
          />
          <SecondaryContactFields inline customer={customer} hasScript={hasScript} />
          <AccountNotesFields inline customer={customer} />
        </Space.$>
      </Form>
    </div>
  );
};
